import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { services as svcApi, usage as usageApi, wallet as walletApi, subscription as subApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Boxes, Activity, Wallet, AlertTriangle, ShieldCheck, HardDrive, Gauge, Layers, Infinity as InfinityIcon } from 'lucide-react';

// Phase 7 — named constants
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 ** 2;
const BYTES_PER_GB = 1024 ** 3;
const USAGE_WARN_PERCENT = 75;     // bar turns amber
const USAGE_CRITICAL_PERCENT = 90; // bar turns red
const RESETS_AT_LABEL = 'midnight UTC';

// Auto-scale bytes -> human display. B (<1KB), KB (<1MB), MB (<1GB), else GB.
function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < BYTES_PER_KB) return `${n} B`;
  if (n < BYTES_PER_MB) return `${(n / BYTES_PER_KB).toFixed(1)} KB`;
  if (n < BYTES_PER_GB) return `${(n / BYTES_PER_MB).toFixed(2)} MB`;
  return `${(n / BYTES_PER_GB).toFixed(2)} GB`;
}

const TIER_COLORS = {
  priority: 'text-emerald-400',
  standard: 'text-cyan-400',
  economy: 'text-slate-400',
  'No subscription': 'text-zinc-400',
};

function barColor(percent) {
  if (percent == null) return 'bg-emerald-500/70';
  if (percent >= USAGE_CRITICAL_PERCENT) return 'bg-red-500';
  if (percent >= USAGE_WARN_PERCENT) return 'bg-amber-500';
  return 'bg-emerald-500/80';
}

function ProgressBar({ percent, testid }) {
  if (percent == null) return null;
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="w-full h-1.5 rounded-full bg-secondary/40 mt-2 overflow-hidden" data-testid={testid}>
      <div className={`h-full ${barColor(clamped)} transition-all`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function UnlimitedBadge({ testid }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400" data-testid={testid}>
      <InfinityIcon className="w-3.5 h-3.5" /> Unlimited
    </span>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [svcData, setSvcData] = useState({});
  const [usageData, setUsageData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [subData, setSubData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    svcApi.list().then(r => setSvcData(r.data.services || {})).catch(() => {});
    usageApi.get().then(r => setUsageData(r.data)).catch(() => {});
    walletApi.get().then(r => setWalletData(r.data)).catch(() => {});
    subApi.current().then(r => setSubData(r.data)).catch(() => {});
    usageApi.summary().then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const enabledServices = user?.enabled_services?.filter(s => s.is_active) || [];
  const tierColors = { economy: 'text-slate-400', standard: 'text-cyan-400', priority: 'text-amber-400' };

  return (
    <div data-testid="overview-page" className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Dashboard</h2>
        <p className="text-muted-foreground mt-1">Welcome back, {user?.full_name || user?.email}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Subscription</p>
                {subData?.has_active ? (
                  <>
                    <p data-testid="active-services-count" className="text-3xl font-bold mt-1 text-emerald-400">{subData.subscription?.services?.length || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{subData.subscription?.days_remaining}d remaining</p>
                  </>
                ) : (
                  <>
                    <p className="text-xl font-bold mt-1 text-muted-foreground">None</p>
                    <p className="text-[10px] text-amber-400">No active subscription</p>
                  </>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subData?.has_active ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                <ShieldCheck className={`w-5 h-5 ${subData?.has_active ? 'text-emerald-400' : 'text-amber-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Requests (24h)</p>
                <p data-testid="requests-24h" className="text-3xl font-bold mt-1">{usageData?.total_requests_24h || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Wallet Balance</p>
                <p data-testid="current-charges" className={`text-3xl font-bold mt-1 font-mono ${(walletData?.balance || 0) <= 0 ? 'text-destructive' : 'text-emerald-400'}`}>
                  ${(walletData?.balance || 0).toFixed(2)}
                </p>
                {(walletData?.balance || 0) <= 0 && <p className="text-[10px] text-destructive flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Add credit</p>}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(walletData?.balance || 0) <= 0 ? 'bg-destructive/15' : 'bg-emerald-500/15'}`}>
                <Wallet className={`w-5 h-5 ${(walletData?.balance || 0) <= 0 ? 'text-destructive' : 'text-emerald-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase 7 — Usage & Limits */}
      <div className="space-y-3" data-testid="usage-and-limits-section">
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold tracking-tight">Usage & Limits</h3>
          <span className="text-xs text-muted-foreground">
            Tier{' '}
            <span data-testid="usage-tier-label" className={`font-semibold ${TIER_COLORS[summary?.tier] || 'text-zinc-400'}`}>
              {summary?.tier ?? '—'}
            </span>
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

          {/* Storage */}
          <Card className="bg-card border-border/50" data-testid="usage-card-storage">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Storage</p>
                <HardDrive className="w-4 h-4 text-cyan-400" />
              </div>
              <p className="text-2xl font-bold font-mono" data-testid="usage-storage-used">
                {summary ? formatBytes(summary.storage.bytes_used) : '—'}
              </p>
              {summary?.storage?.limit_bytes == null ? (
                <UnlimitedBadge testid="usage-storage-unlimited" />
              ) : (
                <p className="text-xs text-muted-foreground">
                  of <span data-testid="usage-storage-limit">{formatBytes(summary.storage.limit_bytes)}</span>
                  {' · '}<span data-testid="usage-storage-percent">{summary.storage.percent ?? 0}%</span>
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="usage-storage-files">
                {summary ? `${summary.storage.file_count} file${summary.storage.file_count === 1 ? '' : 's'}` : ''}
              </p>
              {summary?.storage?.limit_bytes != null && (
                <ProgressBar percent={summary.storage.percent} testid="usage-storage-bar" />
              )}
            </CardContent>
          </Card>

          {/* Requests today */}
          <Card className="bg-card border-border/50" data-testid="usage-card-requests-today">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Requests Today</p>
                <Activity className="w-4 h-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-bold font-mono" data-testid="usage-requests-count">
                {summary?.requests_today?.count == null && summary?.requests_today?.error
                  ? 'Unavailable'
                  : (summary?.requests_today?.count ?? '—').toLocaleString?.() ?? (summary?.requests_today?.count ?? '—')}
              </p>
              {summary?.requests_today?.limit == null ? (
                <UnlimitedBadge testid="usage-requests-unlimited" />
              ) : (
                <p className="text-xs text-muted-foreground">
                  of <span data-testid="usage-requests-limit">{summary.requests_today.limit.toLocaleString()}</span>
                  {summary?.requests_today?.percent != null && (
                    <> {' · '}<span data-testid="usage-requests-percent">{summary.requests_today.percent}%</span></>
                  )}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">Resets at {RESETS_AT_LABEL}</p>
              {summary?.requests_today?.limit != null && summary?.requests_today?.percent != null && (
                <ProgressBar percent={summary.requests_today.percent} testid="usage-requests-bar" />
              )}
            </CardContent>
          </Card>

          {/* Active jobs */}
          <Card className="bg-card border-border/50" data-testid="usage-card-active-jobs">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Jobs</p>
                <Gauge className="w-4 h-4 text-amber-400" />
              </div>
              <p className="text-2xl font-bold font-mono" data-testid="usage-jobs-active">
                {summary?.concurrent_jobs?.active ?? '—'}
              </p>
              {summary?.concurrent_jobs?.limit == null ? (
                <UnlimitedBadge testid="usage-jobs-unlimited" />
              ) : (
                <p className="text-xs text-muted-foreground">
                  of <span data-testid="usage-jobs-limit">{summary.concurrent_jobs.limit}</span> concurrent
                </p>
              )}
              {summary?.concurrent_jobs?.limit != null && (
                <ProgressBar percent={summary.concurrent_jobs.percent} testid="usage-jobs-bar" />
              )}
            </CardContent>
          </Card>

          {/* Tier & services */}
          <Card className="bg-card border-border/50" data-testid="usage-card-tier">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Your Services</p>
                <Layers className="w-4 h-4 text-violet-400" />
              </div>
              <p className={`text-2xl font-bold ${TIER_COLORS[summary?.tier] || 'text-zinc-400'}`} data-testid="usage-tier-big">
                {summary?.tier ?? '—'}
              </p>
              {summary?.services?.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="usage-services-list">
                  {summary.services.map((s) => (
                    <Badge
                      key={s.service}
                      variant="outline"
                      className={`text-[10px] ${TIER_COLORS[s.latency_tier] || 'text-slate-400'}`}
                      data-testid={`usage-service-${s.service}`}
                    >
                      {s.service}·{s.latency_tier}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">No active services</p>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      {enabledServices.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Active Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {enabledServices.map(s => {
                const info = svcData[s.service];
                const usageSvc = usageData?.by_service_24h?.find(u => u.service === s.service);
                return (
                  <div key={s.service} data-testid={`active-service-${s.service}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/30">
                    <div>
                      <p className="font-medium text-sm">{info?.name || s.service}</p>
                      <p className="text-xs text-muted-foreground">{usageSvc?.requests || 0} requests (24h)</p>
                    </div>
                    <Badge variant="outline" className={`text-xs ${tierColors[s.latency_tier] || 'text-slate-400'}`}>
                      {s.latency_tier}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {usageData?.by_service_24h?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Usage Summary (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usageData.by_service_24h.map(u => (
                <div key={u.service} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                  <div>
                    <p className="font-medium text-sm">{svcData[u.service]?.name || u.service}</p>
                    <p className="text-xs text-muted-foreground">{u.avg_latency_ms}ms avg latency</p>
                  </div>
                  <span className="font-mono text-sm">{u.requests} reqs</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
